import { Button, CircularProgress } from '@civilgrid/ui/components';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import type React from 'react';
import { memo, useEffect, useRef } from 'react';
import { useSnapshot } from 'valtio';
import { listProjectCustomData } from '~/api/projectCustomData';
import { Action } from '~/authorizations';
import { useActionPermission } from '~/authorizations/useActionPermission';
import Icon from '~/components2/Icon';
import Popover from '~/components2/Popover/Popover';
import { useExportMultiFormatEnabled } from '~/hooks/useExportMultiFormatEnabled';
import { useExportLabels } from '~/hooks/useExportTerminologyEnabled';
import { customDataQueryKey } from '~/pages/projectDetails/filesAndMedia/CustomDataSection';
import type { ProjectDetailsParams } from '..';
import { EXPORT_TAB_ID } from '../exportTabIds';
import { CUSTOM_DATA_LAYER_PREFIX } from '../map/customDataLayers.helpers';
import { downloadFile, type ProjectDownload, statusPillStyles } from './common';
import { getLayerLabel } from './labels';
import { loadExportHistory } from './loadTelemetry';
import { getDownloadStatus } from './status';
import downloadStore from './store';

// download history layers
export function MapChildren() {
  const { downloads, hoveredDownload } = useSnapshot(downloadStore);

  return (
    <>
      {/* we can reenable this if we let users select the download area again */}
      {/* {downloads.length > 0 &&
        downloads.map((download) => {
          const geometry = download.downloadArea as Geometry;
          const isHovered = download._id === hoveredDownload;

          return (
            <Source
              key={download._id}
              id={download._id}
              type="geojson"
              data={{
                type: 'Feature',
                geometry: geometry,
                properties: {},
              }}
            >
              <Layer
                type="fill"
                paint={{
                  'fill-opacity': isHovered ? 0.4 : 0.1,
                  'fill-color': isHovered ? '#F27E31' : '#DAE3F2',
                }}
              />
              <Layer
                type="line"
                paint={{
                  'line-width': isHovered ? 3 : 2,
                  'line-color': isHovered ? '#F27E31' : '#DAE3F2',
                }}
              />
            </Source>
          );
        })} */}
    </>
  );
}

const StatusPill: React.FC<{ download: ProjectDownload }> = ({ download }) => {
  const basePillStyle = `flex flex-row items-center justify-center space-x-2 rounded-full py-2 px-4 text-xs text-center leading-none cursor-pointer w-5/6`;
  // Signing is a round-trip, so a second click before it lands would sign and count the same
  // download twice. A ref rather than state: two clicks in one tick would both read a stale `false`
  // before React re-rendered.
  const downloading = useRef(false);

  switch (getDownloadStatus(download)) {
    case 'pending':
      return (
        <div
          data-testid="download-status-pending"
          className={`${basePillStyle} ${statusPillStyles.pendingBg} flex flex-row items-center justify-center space-x-2`}
        >
          <div className={`${statusPillStyles.pendingText}`}>Pending</div>
          <CircularProgress indeterminate className={statusPillStyles.pendingText} />
        </div>
      );

    case 'download':
      return (
        <div
          data-testid="download-status-download"
          onClick={(e) => {
            if (downloading.current) return;

            downloading.current = true;
            void downloadFile(e, download).finally(() => {
              downloading.current = false;
            });
          }}
          className={`${basePillStyle} ${statusPillStyles.downloadBg}`}
        >
          <div className={`${statusPillStyles.downloadText}`}>Download</div>
          <Icon className={`${statusPillStyles.downloadText}`} icon="download" />
        </div>
      );

    // A succeeded export that wrote no file: the selected layers held no data. The row stays in the
    // history so every requested format is accounted for, rather than silently missing.
    case 'no-data':
      return (
        <div
          data-testid="download-status-no-data"
          className={`${basePillStyle} ${statusPillStyles.noDataBg} ${statusPillStyles.noDataText}`}
        >
          <div>No data</div>
        </div>
      );

    case 'failed':
      return (
        <div
          data-testid="download-status-failed"
          className={`${basePillStyle} ${statusPillStyles.failedBg} ${statusPillStyles.failedText}`}
        >
          <div>Failed</div>
        </div>
      );

    default:
      return null;
  }
};

const DownloadRow: React.FC<{ download: ProjectDownload }> = memo(({ download }) => {
  const handleMouseEnter = () => {
    downloadStore.setHoveredDownload(download._id);
  };

  const handleMouseLeave = () => {
    downloadStore.setHoveredDownload(null);
  };

  return (
    <tr
      className="cursor-pointer border-b border-gray-200 hover:bg-[#FEF2EA]"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <td className="p-2">
        <div className="flex flex-col space-y-1">
          <div>{DateTime.fromISO(download.createdAt).toFormat('MM/dd/yyyy')}</div>
          <div className="text-2xs">{DateTime.fromISO(download.createdAt).toFormat('hh:mm a')}</div>
        </div>
      </td>
      <td className="p-2">{download.user.name ?? download.user.email ?? 'N/A'}</td>
      <td className="p-2">{download.downloadType}</td>
      <td className="p-2 text-center ">{download.areaInAcres ? download.areaInAcres.toFixed(2) : 'N/A'}</td>
      <td>
        <StatusPill download={download} />
      </td>
      <td>
        <DownloadInfo download={download} />
      </td>
    </tr>
  );
});

type DownloadInfoProps = {
  download: ProjectDownload;
};

const DownloadInfo = ({ download }: DownloadInfoProps) => {
  const labels = useExportLabels();
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;

  // Only fetch custom-data rows when this download actually references one — the cache is
  // shared with the map/files panel (customDataQueryKey), so it's usually warm already.
  const hasCustomDataLayer = download.layers?.some((l) => l.startsWith(CUSTOM_DATA_LAYER_PREFIX)) ?? false;
  const { data: customDataRows } = useQuery({
    queryKey: customDataQueryKey(projectId),
    queryFn: () => listProjectCustomData(projectId),
    enabled: hasCustomDataLayer && Boolean(projectId),
    refetchOnWindowFocus: false,
    retry: false,
  });
  const customDataById = Object.fromEntries((customDataRows ?? []).map((row) => [row.id, row]));

  return (
    <Popover
      sideOffset={10}
      side="bottom"
      align="end"
      trigger={
        <button
          type="button"
          aria-label={`Export details, ${download.downloadType} ${DateTime.fromISO(download.createdAt).toFormat('MM/dd/yyyy hh:mm a')}`}
          data-testid={`download-history-details-trigger-${download.id}`}
          className="flex w-1/6 appearance-none items-center justify-center border-0 bg-transparent p-0 rounded focus-ring"
          onClick={(e) => e.stopPropagation()}
        >
          <Icon icon="help" />
        </button>
      }
    >
      {(_closePopover) => (
        <div
          className="bg-opacity-85 relative max-h-[30vh] max-w-[50vw] overflow-y-auto rounded-lg border bg-white bg-opacity-90 p-2 text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          <table className="w-full table-auto">
            <thead>
              <tr className="text-left text-xs font-bold">
                <th>{labels.historyLayersPopoverHeader}</th>
              </tr>
            </thead>
            <tbody>
              {download.layers?.map((downloadLayer) => {
                return (
                  <tr key={downloadLayer} className="flex flex-col space-y-1">
                    <td>{getLayerLabel(downloadLayer, { customDataById })}</td>
                  </tr>
                );
              })}
              <tr>
                <td className="py-2"></td>
              </tr>
              <tr className="text-left font-bold">
                <th>Projection</th>
              </tr>
              <tr>
                <td>{`${download.projection?.title}`}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Popover>
  );
};

export function TabDetails() {
  const { downloads } = useSnapshot(downloadStore) as typeof downloadStore;
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const navigate = useNavigate();
  const labels = useExportLabels();
  const historyRelocated = useExportMultiFormatEnabled();
  // The list only needs ViewProjectDetails; the form these buttons open needs DownloadData.
  const canDownloadData = useActionPermission(Action.DownloadData);

  // Only reached by saved links once the history renders under the export form.
  useEffect(() => {
    if (historyRelocated) {
      navigate({ to: '/projects/$projectId/$tabId', params: { projectId, tabId: EXPORT_TAB_ID }, replace: true });
    }
  }, [historyRelocated, navigate, projectId]);

  useEffect(() => {
    if (historyRelocated) return;

    void loadExportHistory(projectId, 'initial');

    // refetch every 10 seconds
    const intervalId = setInterval(() => {
      void loadExportHistory(projectId, 'poll');
    }, 10000);

    return () => clearInterval(intervalId); // Clean up interval on unmount
  }, [projectId, historyRelocated]);

  useEffect(() => {
    // reset download store when unmounting
    return () => {
      downloadStore.reset();
    };
  }, []);

  // Rendering the table here would flash it before the redirect above lands.
  if (historyRelocated) return null;

  if (downloads.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center space-y-2">
        <div>{labels.historyEmptyState}</div>
        {canDownloadData && (
          <Button
            variant="secondary"
            data-testid="exports-history-new-export-button"
            onClick={() => navigate({ to: '/projects/$projectId/$tabId', params: { projectId, tabId: EXPORT_TAB_ID } })}
          >
            {labels.historyEmptyStateButton}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col space-y-4">
      {canDownloadData && (
        <div className="flex justify-end">
          <Button
            variant="secondary"
            data-testid="downloads-back-button"
            onClick={() => navigate({ to: '/projects/$projectId/$tabId', params: { projectId, tabId: EXPORT_TAB_ID } })}
          >
            Back
          </Button>
        </div>
      )}
      <table className="w-full table-auto">
        <thead className="bg-gray-100">
          <tr className="text-left font-bold text-black">
            <th className="p-2">Date</th>
            <th className="p-2">{labels.historyRequestedByColumn}</th>
            <th className="p-2">Format</th>
            <th className="p-2">Area (ac)</th>
            <th className="p-2">Status</th>
            <th className="p-2"></th>
          </tr>
        </thead>
        <tbody>
          {downloads.map((download) => (
            <DownloadRow key={download.id} download={download} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
